import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useDigitalFormList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refDigitalFormTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'name',
      label: 'Form Name',
      sortable: true,
    },
    {
      key: 'type',
      label: 'Form Type',
      sortable: true,
    },
    {
      key: 'formNumber',
      label: 'Form Code',
      sortable: true,
    },
    {
      key: 'revision',
      label: 'Version',
      sortable: true,
    },
    {
      key: 'createdAt',
      label: 'Date added',
      sortable: true,
    },
    { key: 'status', sortable: true },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalDigitalForms = ref(0)
  const searchQuery = ref(route.query.search || '')
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const sortBy = ref(route.query.sortBy ?? 'name')
  const isSortDirDesc = ref(route.query.sortDesc === 'true')
  const toDeletedID = ref(null)
  let initialLoad = true
  const currentDataLength = ref(0)

  const dataMeta = computed(() => {
    const localItemsCount = refDigitalFormTable.value ? refDigitalFormTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalDigitalForms.value,
    }
  })

  const refetchData = () => {
    refDigitalFormTable.value.refresh()
  }

  watch([searchQuery], () => {
    currentPage.value = 1
    refetchData()
  })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchDigitalForms = (ctx, callback) => {
    store
      .dispatch('app-digital-form/fetchDigitalForms', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
      },
      { root: true })
      .then(response => {
        const { forms, total } = response.data
        currentDataLength.value = forms.length
        callback(forms)
        totalDigitalForms.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deleteDigitalForm = () => {
    store
      .dispatch('app-digital-form/deleteDigitalForm', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveDigitalFormStatusVariant = status => {
    if (status === 'In use') return 'success'
    if (status === 'Archived') return 'secondary'
    return 'primary'
  }

  return {
    fetchDigitalForms,
    tableColumns,
    perPage,
    currentPage,
    totalDigitalForms,
    dataMeta,
    perPageOptions,
    sortBy,
    isSortDirDesc,
    refDigitalFormTable,

    searchQuery,

    resolveDigitalFormStatusVariant,
    refetchData,
    currentDataLength,

    toDeletedID,
    deleteDigitalForm,
  }
}
